import { Box, Button, Skeleton, Typography } from '@mui/material';
import { type ReactElement } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import ErrorState from 'app/shared-components/common/ErrorState';

import type { GlobalSectionType } from './hooks/useGetGlobalSectionTypes';
import { useGetGlobalSectionTypes } from './hooks/useGetGlobalSectionTypes';
import type { WebsiteSection } from './hooks/useGetWebsiteSections';
import { useGetWebsiteSections } from './hooks/useGetWebsiteSections';

interface HeaderAndFooterTabProps {
  websiteId: string;
}

const HeaderAndFooterTab = ({ websiteId }: HeaderAndFooterTabProps): ReactElement => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const organisationId = searchParams.get('organisationId') ?? '';
  const {
    sections,
    isLoading: isSectionsLoading,
    isError: isSectionsError,
    error: sectionsError,
  } = useGetWebsiteSections({ websiteId });
  const {
    sectionTypes,
    isLoading: isSectionTypesLoading,
    isError: isSectionTypesError,
  } = useGetGlobalSectionTypes(organisationId);

  if (isSectionsError) {
    return (
      <ErrorState
        title="Failed to load sections"
        subtitle={sectionsError instanceof Error ? sectionsError.message : 'Try again later'}
      />
    );
  }

  const isLoading = isSectionsLoading || isSectionTypesLoading;
  const existingSectionTypeIds = new Set(sections.map((s) => s.sectionTypeId));
  const availableSectionTypes = sectionTypes.filter((st) => !existingSectionTypeIds.has(st.id));

  const handleAdd = (sectionType: GlobalSectionType) => {
    navigate(
      {
        pathname: '/plus-websites/sections/create',
        search: new URLSearchParams({
          websiteId,
          organisationId: searchParams.get('organisationId') ?? '',
        }).toString(),
      },
      {
        state: {
          sectionDisplayName: sectionType.displayName,
          sectionTypeId: sectionType.id,
          sectionTypeVersion: sectionType.version,
        },
      }
    );
  };

  return (
    <Box className="vtwo:flex vtwo:flex-col vtwo:gap-3 vtwo:px-16 vtwo:pb-16">
      {isLoading &&
        Array.from({ length: 3 }).map((_, i) => (
          <Box
            key={i}
            className="vtwo:flex vtwo:justify-between vtwo:items-center vtwo:px-6 vtwo:py-5 vtwo:rounded-xl vtwo:border-2 vtwo:border-plus-border vtwo:bg-white"
          >
            <Skeleton variant="text" width={160} height={24} />
            <Skeleton variant="rounded" width={72} height={30} />
          </Box>
        ))}

      {!isLoading &&
        sections.map((section) => (
          <SectionRow
            key={section.id}
            section={section}
            onModify={() =>
              navigate({
                pathname: `/plus-websites/sections/${section.id}/edit`,
                search: new URLSearchParams({
                  websiteId,
                  organisationId: searchParams.get('organisationId') ?? '',
                }).toString(),
              })
            }
          />
        ))}

      {!isLoading && isSectionTypesError && (
        <Typography variant="body2" className="vtwo:text-red-600">
          Failed to load available section types
        </Typography>
      )}

      {!isLoading &&
        !isSectionTypesError &&
        availableSectionTypes.map((sectionType) => (
          <AvailableSectionTypeRow
            key={sectionType.id}
            sectionType={sectionType}
            onAdd={() => handleAdd(sectionType)}
          />
        ))}
    </Box>
  );
};

function SectionRow({
  section,
  onModify,
}: {
  section: WebsiteSection;
  onModify: () => void;
}): ReactElement {
  return (
    <Box className="vtwo:flex vtwo:justify-between vtwo:items-center vtwo:px-6 vtwo:py-5 vtwo:rounded-xl vtwo:border-2 vtwo:border-plus-border vtwo:bg-white">
      <Typography className="vtwo:font-bold">{section.displayName}</Typography>
      <Button
        variant="outlined"
        onClick={onModify}
        className="vtwo:rounded-lg vtwo:px-6 vtwo:py-1 vtwo:text-featured vtwo:border-featured vtwo:hover:border-featured-hover vtwo:hover:bg-featured/[0.04]"
      >
        Modify
      </Button>
    </Box>
  );
}

interface AvailableSectionTypeRowProps {
  sectionType: GlobalSectionType;
  onAdd: () => void;
}

function AvailableSectionTypeRow({
  sectionType,
  onAdd,
}: AvailableSectionTypeRowProps): ReactElement {
  return (
    <Box className="vtwo:flex vtwo:justify-between vtwo:items-center vtwo:px-6 vtwo:py-5 vtwo:rounded-xl vtwo:border-2 vtwo:border-plus-border vtwo:bg-white">
      <Typography className="vtwo:font-bold">{sectionType.displayName}</Typography>
      <Button
        variant="outlined"
        onClick={onAdd}
        className="vtwo:rounded-lg vtwo:px-6 vtwo:py-1 vtwo:text-featured vtwo:border-featured vtwo:hover:border-featured-hover vtwo:hover:bg-featured/[0.04]"
      >
        Add
      </Button>
    </Box>
  );
}

export default HeaderAndFooterTab;
